import {
  useRef,
  useState,
  useEffect,
  useContext,
  createContext,
  type FC,
  type PropsWithChildren,
} from 'react';
import {
  useModalQueryParam,
  useQueryParamModalVisibility,
} from '@shared/lib';
import {
  selectFullProfile,
  selectIsAuthenticated,
  useSessionSelector
} from '@entities/session';
import {
  generateHash,
  loadTawkScript,
  cleanupExistingLiveChatElements
} from '../lib';
import {
  API_KEY
} from '../config';

export type LoginLiveChatOptions = {
  userId: string;
  name: string;
  email: Email;
  phone: Maybe<Phone>;
};

export type UseLiveChat = {
  closeChat(): void;
  logoutChat(): void;
  loginChat(options: LoginLiveChatOptions): Promise<void>;
};

export const LiveChatContext = createContext<Maybe<UseLiveChat>>(null);

export const useLiveChat = (): Maybe<UseLiveChat> => {
  return useContext(LiveChatContext);
};

export const LiveChatProvider: FC<PropsWithChildren> = ({ children }) => {
  const isOpened = useRef<boolean>(false);
  const isLoggedIn = useRef<boolean>(false);
  const isScriptLoading = useRef<boolean>(false);
  const tawkMessengerRef = useRef<Maybe<TawkAPI>>(null);
  const closeChatFunctionRef = useRef<() => void>(() => { });
  const [isScriptLoaded, setIsScriptLoaded] = useState<boolean>(false);

  const { closeModal } = useModalQueryParam();
  const openLiveChat = useQueryParamModalVisibility('chat');

  const userFullProfile = useSessionSelector(selectFullProfile);
  const isAuthenticated = useSessionSelector(selectIsAuthenticated);

  const loginChat = async ({
    userId,
    name,
    email,
    phone
  }: LoginLiveChatOptions): Promise<void> => {

    if (!isLoggedIn.current) {
      isLoggedIn.current = true;
      const hash = generateHash(userId, API_KEY);
      await tawkMessengerRef.current?.login(
        {
          hash,
          userId,
          name,
          email,
          phone,
        },
        (error: any) => {
          console.error(`login: ${error}`);
          isLoggedIn.current = false;
        }
      );
    }
  };

  const logoutChat = async (): Promise<void> => {
    cleanupExistingLiveChatElements();

    tawkMessengerRef.current?.logout(error => {
      isLoggedIn.current = false;
      console.error('TAWK Chat Error:', error);
    });
  };

  const closeChat = (): void => {
    isOpened.current = false;
    // tawkMessengerRef.current?.minimize();
    // tawkMessengerRef.current?.hideWidget();
    closeModal('chat');
  };

  // eslint-disable-next-line
  const openChat = async (): Promise<void> => {
    window.Tawk_API = window.Tawk_API || {};

    if (userFullProfile?.userId) {
      const hash = generateHash(userFullProfile.userId, API_KEY);
      tawkMessengerRef.current?.setAttributes({
        hash,
        name: userFullProfile?.nickname ?? '',
        email: userFullProfile?.email ?? '',
        phone: userFullProfile?.phone ?? '',
        userId: userFullProfile?.userId ?? ''
      });
    }

    setTimeout(() => {
      tawkMessengerRef.current?.showWidget();
      tawkMessengerRef.current?.maximize();
      tawkMessengerRef.current?.start();
    }, 1500);
  };

  useEffect(() => {
    cleanupExistingLiveChatElements();

    if (isAuthenticated && !isScriptLoading.current || !isScriptLoaded) {
      isScriptLoading.current = true;
      loadTawkScript(() => {
        window.Tawk_API = window.Tawk_API || {};
        window.Tawk_API.autoStart = false;
        window.Tawk_LoadStart = new Date();

        tawkMessengerRef.current = window.Tawk_API;

        tawkMessengerRef.current!.onLoad = () => {
          isScriptLoading.current = false;
          setIsScriptLoaded(true);
          tawkMessengerRef.current?.start();
        };

        tawkMessengerRef.current!.onChatMinimized = () => {
          isOpened.current = false;
          setTimeout(() => {
            tawkMessengerRef.current?.minimize();
            tawkMessengerRef.current?.hideWidget();
            closeChatFunctionRef.current?.();
          }, 800);
        };
      });
    }
    // eslint-disable-next-line
  }, [openLiveChat, isAuthenticated]);

  useEffect(() => {
    if (openLiveChat) {
      closeChatFunctionRef.current = closeChat;
      openChat();
    }
    // eslint-disable-next-line
  }, [openLiveChat, isAuthenticated]);

  return (
    <LiveChatContext.Provider
      value={{
        loginChat,
        closeChat,
        logoutChat
      }}>
      {children}
    </LiveChatContext.Provider>
  );
};